import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './tenant-bids.reducer';

export const TenantBids = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const tenantBidsList = useAppSelector(state => state.tenantBids.entities);
  const loading = useAppSelector(state => state.tenantBids.loading);
  const totalItems = useAppSelector(state => state.tenantBids.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="tenant-bids-heading" data-cy="TenantBidsHeading">
        <Translate contentKey="tinyMedicRestApp.tenantBids.home.title">Tenant Bids</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.tenantBids.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/tenant-bids/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.tenantBids.home.createLabel">Create new Tenant Bids</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.tenantBids.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {tenantBidsList && tenantBidsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.id">Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('status')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.status">Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('status')} />
                </th>
                <th className="hand" onClick={sort('createdById')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.createdById">Created By Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdById')} />
                </th>
                <th className="hand" onClick={sort('createdByType')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.createdByType">Created By Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createdByType')} />
                </th>
                <th className="hand" onClick={sort('billFor')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.billFor">Bill For</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('billFor')} />
                </th>
                <th className="hand" onClick={sort('patientType')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.patientType">Patient Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('patientType')} />
                </th>
                <th className="hand" onClick={sort('bidSubmittedDate')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.bidSubmittedDate">Bid Submitted Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bidSubmittedDate')} />
                </th>
                <th className="hand" onClick={sort('bidAcceptedDate')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.bidAcceptedDate">Bid Accepted Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bidAcceptedDate')} />
                </th>
                <th className="hand" onClick={sort('totalCost')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.totalCost">Total Cost</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalCost')} />
                </th>
                <th className="hand" onClick={sort('discount')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.discount">Discount</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('discount')} />
                </th>
                <th className="hand" onClick={sort('cgst')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.cgst">Cgst</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cgst')} />
                </th>
                <th className="hand" onClick={sort('sgst')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.sgst">Sgst</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sgst')} />
                </th>
                <th className="hand" onClick={sort('roundOffVal')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.roundOffVal">Round Off Val</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('roundOffVal')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('lastUpdated')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.lastUpdated">Last Updated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastUpdated')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampEntry')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.timestampEntry">Timestamp Entry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampEntry')} />
                </th>
                <th className="hand" onClick={sort('homeDeliveryAvailable')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.homeDeliveryAvailable">Home Delivery Available</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('homeDeliveryAvailable')} />
                </th>
                <th className="hand" onClick={sort('deliveryTime')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.deliveryTime">Delivery Time</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('deliveryTime')} />
                </th>
                <th className="hand" onClick={sort('deliveryCost')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.deliveryCost">Delivery Cost</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('deliveryCost')} />
                </th>
                <th className="hand" onClick={sort('grossAmount')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.grossAmount">Gross Amount</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('grossAmount')} />
                </th>
                <th className="hand" onClick={sort('bidNumber')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.bidNumber">Bid Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bidNumber')} />
                </th>
                <th className="hand" onClick={sort('bidDate')}>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.bidDate">Bid Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bidDate')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.tinymedicPatientOrder">Tinymedic Patient Order</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.tenantBids.tenant">Tenant</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenantBidsList.map((tenantBids, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/tenant-bids/${tenantBids.id}`} color="link" size="sm">
                      {tenantBids.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.BidStatus.${tenantBids.status}`} />
                  </td>
                  <td>{tenantBids.createdById}</td>
                  <td>{tenantBids.createdByType}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.BillFor.${tenantBids.billFor}`} />
                  </td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.PatientType.${tenantBids.patientType}`} />
                  </td>
                  <td>{tenantBids.bidSubmittedDate}</td>
                  <td>{tenantBids.bidAcceptedDate}</td>
                  <td>{tenantBids.totalCost}</td>
                  <td>{tenantBids.discount}</td>
                  <td>{tenantBids.cgst}</td>
                  <td>{tenantBids.sgst}</td>
                  <td>{tenantBids.roundOffVal}</td>
                  <td>{tenantBids.active ? 'true' : 'false'}</td>
                  <td>{tenantBids.cursor}</td>
                  <td>{tenantBids.lastUpdated}</td>
                  <td>{tenantBids.microSec}</td>
                  <td>{tenantBids.timestampEntry}</td>
                  <td>{tenantBids.homeDeliveryAvailable ? 'true' : 'false'}</td>
                  <td>{tenantBids.deliveryTime}</td>
                  <td>{tenantBids.deliveryCost}</td>
                  <td>{tenantBids.grossAmount}</td>
                  <td>{tenantBids.bidNumber}</td>
                  <td>{tenantBids.bidDate}</td>
                  <td>
                    {tenantBids.tinymedicPatientOrder ? (
                      <Link to={`/tinymedic-patient-order/${tenantBids.tinymedicPatientOrder.id}`}>
                        {tenantBids.tinymedicPatientOrder.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>{tenantBids.tenant ? <Link to={`/tenant/${tenantBids.tenant.id}`}>{tenantBids.tenant.id}</Link> : ''}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/tenant-bids/${tenantBids.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/tenant-bids/${tenantBids.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/tenant-bids/${tenantBids.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.tenantBids.home.notFound">No Tenant Bids found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={tenantBidsList && tenantBidsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default TenantBids;
